import type { ResyncRunner } from './types';
import type { AlignmentResult } from '../../core';
import { decodeToMono } from '../audio/decodeToMono';
import { RESYNC_TARGET_RATE } from './pcmRate';
import type { ResyncRequest, ResyncResponse } from '../workers/resync.worker';

/**
 * Default {@link ResyncRunner}: reads the file's bytes, decodes them to mono
 * PCM on the MAIN thread, then hands the PCM plus the cue on/off intervals to
 * the resync Web Worker (alignment off the main thread), and resolves with
 * the proposed offset/rate.
 *
 * cue-forge-5nj: decode used to happen inside the worker, but
 * `OfflineAudioContext` does not exist in dedicated workers on current
 * browsers (verified empirically), so `decodeToMono` now runs here instead —
 * still entirely on-device, never uploaded, before the worker is even
 * spawned. A decode failure therefore rejects this promise directly (no
 * worker to terminate), preserving this runner's existing "rejects with an
 * `Error`" contract that `TimingControls.tsx`'s `AutoResyncTool` already
 * catches and surfaces.
 */
export const workerRunner: ResyncRunner = async (doc, file, opts = {}) => {
  const audio = await file.arrayBuffer();
  const pcm = await decodeToMono(audio, RESYNC_TARGET_RATE);
  const worker = new Worker(new URL('../workers/resync.worker.ts', import.meta.url), {
    type: 'module',
  });
  try {
    return await new Promise<AlignmentResult>((resolve, reject) => {
      worker.onmessage = (e: MessageEvent<ResyncResponse>) => {
        if (e.data.type === 'error') reject(new Error(e.data.message));
        else resolve(e.data.result);
      };
      worker.onerror = (e) => reject(new Error(e.message || 'Resync worker failed'));
      worker.postMessage(
        {
          pcm,
          cues: doc.cues.map((c) => ({ startMs: c.startMs, endMs: c.endMs })),
          estimateRate: opts.estimateRate ?? false,
        } satisfies ResyncRequest,
        [pcm.buffer],
      );
    });
  } finally {
    worker.terminate();
  }
};
